import type { FreeTextOverlay, LabelOverlay, OutputSpec, TitleCardOverlay } from '@shared/types'

/**
 * UI MK2 第2段 2a — プレビュー直接操作の純関数群。
 *
 * Title/Label の離散スロット座標は「推測で置かない」（task spec §0）— server の
 * 配置規則をそのまま写す:
 *   - `server/src/telop/kinds/titleCard.ts`: `anchorX = position==='right' ? width*0.72 : width*0.5`,
 *     `mainCenterY = height*0.46`（テキストブロックの垂直中心）
 *   - `server/src/telop/kinds/label.ts`'s `anchorFor`: left-upper = (width*0.06, height*0.16) /
 *     left-lower = (width*0.06, height*0.7) / bottom = (width*0.5, height*0.8)（bottom は
 *     align:'center'、それ以外は align:'left' — anchor.y はテキストブロックの「上端」）
 *
 * ここでの座標は 0..1 正規化（output.width/height を掛ける前）。実ピクセルへの変換は
 * 呼び出し側（Preview コンポーネント）が output サイズを掛けて行う。
 */

export interface NormPoint {
  xNorm: number
  yNorm: number
}

// ---------- Title slots ----------
export type TitleSlot = TitleCardOverlay['position']

/** server/src/telop/kinds/titleCard.ts の anchorX / mainCenterY を 0..1 に正規化した写し。 */
export const TITLE_SLOT_ANCHOR: Record<TitleSlot, NormPoint> = {
  center: { xNorm: 0.5, yNorm: 0.46 },
  right: { xNorm: 0.72, yNorm: 0.46 },
}

export const TITLE_SLOTS: TitleSlot[] = ['center', 'right']

// ---------- Label slots ----------
export type LabelSlot = LabelOverlay['position']

/** server/src/telop/kinds/label.ts の anchorFor を 0..1 に正規化した写し。 */
export const LABEL_SLOT_ANCHOR: Record<LabelSlot, NormPoint> = {
  'left-upper': { xNorm: 0.06, yNorm: 0.16 },
  'left-lower': { xNorm: 0.06, yNorm: 0.7 },
  bottom: { xNorm: 0.5, yNorm: 0.8 },
}

export const LABEL_SLOTS: LabelSlot[] = ['left-upper', 'left-lower', 'bottom']

/** label の水平アライン。bottom だけ center、それ以外は anchor.x が左端（align:'left'）。 */
export function labelSlotAlign(slot: LabelSlot): 'left' | 'center' {
  return slot === 'bottom' ? 'center' : 'left'
}

function nearestOf<K extends string>(anchors: Record<K, NormPoint>, keys: K[], p: NormPoint): K {
  let best = keys[0]
  let bestDist = Infinity
  for (const key of keys) {
    const a = anchors[key]
    const d = Math.hypot(a.xNorm - p.xNorm, a.yNorm - p.yNorm)
    if (d < bestDist) {
      bestDist = d
      best = key
    }
  }
  return best
}

/** ポインタ位置（0..1 正規化）に最も近い Title スロットを返す。 */
export function nearestTitleSlot(p: NormPoint): TitleSlot {
  return nearestOf(TITLE_SLOT_ANCHOR, TITLE_SLOTS, p)
}

/** ポインタ位置（0..1 正規化）に最も近い Label スロットを返す。 */
export function nearestLabelSlot(p: NormPoint): LabelSlot {
  return nearestOf(LABEL_SLOT_ANCHOR, LABEL_SLOTS, p)
}

// ---------- FreeText free drag ----------
/** task spec §2-1: 画面外へ出さないよう 0.02..0.98 にクランプ。 */
export const FREE_TEXT_MIN_NORM = 0.02
export const FREE_TEXT_MAX_NORM = 0.98
/** task spec §2-1: Shift でスナップ（0.05 刻み）。 */
export const FREE_TEXT_SNAP_STEP = 0.05

export function clampFreeTextNorm(v: number): number {
  return Math.min(FREE_TEXT_MAX_NORM, Math.max(FREE_TEXT_MIN_NORM, v))
}

export function snapToGrid(v: number, step = FREE_TEXT_SNAP_STEP): number {
  return Math.round(v / step) * step
}

/** ドラッグ終了時に FreeText の x/y へ適用する最終値（スナップ→クランプの順）。 */
export function resolveFreeTextPosition(p: NormPoint, shiftSnap: boolean): NormPoint {
  const x = clampFreeTextNorm(shiftSnap ? snapToGrid(p.xNorm) : p.xNorm)
  const y = clampFreeTextNorm(shiftSnap ? snapToGrid(p.yNorm) : p.yNorm)
  return { xNorm: x, yNorm: y }
}

// ---------- interaction hit boxes (approximate — click/drag targets only) ----------
/**
 * これらは「絵」ではなく「当たり判定」用の近似ボックス（0..1 正規化）。実際の見た目
 * は server 描画の PNG（またはその CSS fallback）がそのまま担う — ここではユーザーが
 * クリック/ドラッグを開始できる程度の合理的な矩形を返すだけで、テキストの正確な
 * bounding box を計算しない（クライアント側にフォント計測手段が無いため。既存の
 * color-grade/fit=contain の近似と同じ思想: "approximation, not pixel-identical"）。
 */
export interface HitBox {
  leftNorm: number
  topNorm: number
  widthNorm: number
  heightNorm: number
}

export function titleHitBox(position: TitleSlot): HitBox {
  const a = TITLE_SLOT_ANCHOR[position]
  const width = 0.68
  const height = 0.52
  return { leftNorm: a.xNorm - width / 2, topNorm: a.yNorm - height / 2, widthNorm: width, heightNorm: height }
}

export function labelHitBox(position: LabelSlot): HitBox {
  const a = LABEL_SLOT_ANCHOR[position]
  const align = labelSlotAlign(position)
  const width = align === 'center' ? 0.7 : 0.42
  const height = 0.14
  const left = align === 'center' ? a.xNorm - width / 2 : a.xNorm - 0.02
  return { leftNorm: left, topNorm: a.yNorm - 0.02, widthNorm: width, heightNorm: height }
}

/**
 * reviewer 差し戻し m1: `server/src/telop/kinds/logo.ts` は
 * `cy = output.height*0.06 + diameter/2`（diameter=150）で円を描く。
 * 1080x1920 基準で正規化すると中心 y ≈ 0.099、円の上端 ≈ 0.06・下端 ≈ 0.138。
 * 旧実装の `topNorm: 0.02` はこの実際の描画位置よりかなり上にズレていた。
 *
 * P10-3 (2026-09-06, 担当B「出力サイズ切替」): 上の 0.055/0.09/0.18 という
 * 定数は「1080x1920 でのみ正しい」— `diameter`(150px) は output サイズに
 * 依らず常に一定の実ピクセル値なので、当たり判定の余白（クリックしやすくする
 * ための拡張）も実ピクセル値で持ち、`output.width`/`output.height` で正規化
 * する。旧定数からの逆算: widthNorm=0.18（@1080）→半幅97.2px（半径75pxに
 * 22.2pxの余白）。topNorm=0.055・heightNorm=0.09（@1920）→上端105.6px
 * （円の実上端115.2pxより9.6px上に拡張）・下端278.4px（実下端265.2pxより
 * 13.2px下に拡張）。この3つの余白定数（PAD_X_PX/PAD_TOP_PX/PAD_BOTTOM_PX）
 * を output=1080x1920 で計算すると旧ハードコード値と厳密に一致する
 * （previewMath.test.ts で回帰確認）。
 */
export function logoHitBox(output: Pick<OutputSpec, 'width' | 'height'>): HitBox {
  const DIAMETER_PX = 150
  const RADIUS_PX = DIAMETER_PX / 2
  const PAD_X_PX = 22.2
  const PAD_TOP_PX = 9.6
  const PAD_BOTTOM_PX = 13.2
  const cyPx = output.height * 0.06 + RADIUS_PX
  const halfWidthPx = RADIUS_PX + PAD_X_PX
  const topPx = cyPx - RADIUS_PX - PAD_TOP_PX
  const bottomPx = cyPx + RADIUS_PX + PAD_BOTTOM_PX
  return {
    leftNorm: 0.5 - halfWidthPx / output.width,
    topNorm: topPx / output.height,
    widthNorm: (2 * halfWidthPx) / output.width,
    heightNorm: (bottomPx - topPx) / output.height,
  }
}

export function endCardHitBox(): HitBox {
  return { leftNorm: 0.14, topNorm: 0.28, widthNorm: 0.72, heightNorm: 0.46 }
}

/**
 * FreeText: 文字数・sizePx から粗く見積もる（正確なテキスト計測はしない）。
 * P10-3: `sizePx` は「1080x1920 出力空間での px」(shared/src/types.ts
 * FreeTextOverlay.sizePx のドキュメントコメント)— server の実描画
 * (telop/kinds/freeText.ts) は `overlay.sizePx` をそのまま output の
 * canvas に描く（プリセット間で短辺が常に1080pxなので見た目の大きさは
 * 揃う。shared/src/outputPresets.ts のコメント参照）。当たり判定もこの
 * 1文字あたりの実ピクセル量を `output.width`/`output.height` で正規化する
 * ように一般化した（旧実装は 1080/1920 に決め打ち）。
 */
export function freeTextHitBox(
  overlay: Pick<FreeTextOverlay, 'text' | 'sizePx' | 'vertical' | 'x' | 'y'>,
  output: Pick<OutputSpec, 'width' | 'height'>,
): HitBox {
  const charCount = Math.max(1, Array.from(overlay.text).length)
  const wPerChar = overlay.sizePx / output.width
  const hPerChar = overlay.sizePx / output.height
  const width = overlay.vertical ? Math.max(0.08, wPerChar * 1.3) : Math.min(0.92, wPerChar * charCount * 0.62 + 0.03)
  const height = overlay.vertical ? Math.min(0.92, hPerChar * charCount * 1.05 + 0.03) : Math.max(0.06, hPerChar * 1.5)
  return { leftNorm: overlay.x - width / 2, topNorm: overlay.y - height / 2, widthNorm: width, heightNorm: height }
}
